import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type NowDashboardView } from '@vello/api-client';
import { ColumnChart, Notice, Panel, SectionTitle, StatCard, Text, Grid, Stack, useAutoRefresh } from '@vello/web-theme';
import { NOW_KPI_IDS, formatNowDelta, hasSupplyData, nowBars, nowKpis, supplyHours } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

/**
 * Visão geral › Agora (D559 A02-HOURLY, doc 22): the selected city's TODAY so far on the city's own clock — the four headline figures with
 * their change against yesterday at the same time, the drivers online now, the rides requested hour by hour (the design bar chart, the
 * current hour in ink, hours still to come left empty) and the real drivers online per hour. Everything is read from the analytics
 * model (`GET /v1/ops/dashboard/now`); when it cannot answer the panel says "unavailable", it never falls back to a number it cannot
 * back. An hour with no presence data is not drawn as zero (a real zero is). The 5-minute zone blocks wait for the wave-10 zone signals (a typed
 * seam on the endpoint, one honest line here). It refreshes on the server's `refreshSeconds` while the tab is visible.
 */
export function NowPanel(): ReactElement {
  const { runtime, scope } = useAdmin();
  const { t, lang } = useT();
  const { refusal: refusalOf } = useVocab();
  const market = scope.current.city;
  const [view, setView] = useState<NowDashboardView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const seq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    if (market === undefined) return;
    const mine = ++seq.current;
    const res = await runtime.ops.nowDashboard(market);
    if (mine !== seq.current) return;
    if (res.ok) {
      setView(res.value);
      setError(undefined);
    } else {
      const own = res.error.reasonCode === 'ANALYTICS_UNAVAILABLE' || res.error.reasonCode === 'ANALYTICS_QUERY_TIMEOUT' ? t('now.unavailable') : refusalOf('refusal.admin.live_map', res.error);
      setError(own ?? t('now.error'));
    }
  }, [runtime, market, t, refusalOf]);

  useEffect(() => {
    setView(undefined);
    setError(undefined);
    void load();
  }, [load]);

  const refreshMs = Math.max(10, view?.refreshSeconds ?? 60) * 1000;
  useAutoRefresh(load, { intervalMs: refreshMs, enabled: market !== undefined });

  if (market === undefined) {
    return (
      <Stack as="section" gap={4} aria-labelledby="now-title">
        <Text as="h2" role="title" id="now-title">{t('now.title')}</Text>
        <Notice tone="neutral">{t('now.needMarket')}</Notice>
      </Stack>
    );
  }

  const nf = new Intl.NumberFormat(lang);
  const kpis = nowKpis(view);
  const bars = nowBars(view);
  const loading = view === undefined && error === undefined;
  const supply = supplyHours(view).filter((h) => !h.future && h.driversOnline !== null);

  return (
    <Stack as="section" gap={4} aria-labelledby="now-title">
      <Text as="h2" role="title" id="now-title">{t('now.title')}</Text>
      <Text as="p" role="caption" tone="secondary">{t('now.subtitle')}</Text>
      {error !== undefined && <Notice tone="warning">{error}</Notice>}

      <Grid min="xs" gap={4}>
        {NOW_KPI_IDS.map((id) => {
          const k = kpis.find((x) => x.id === id);
          return (
            <StatCard
              key={id}
              variant="overview"
              label={t(`now.kpi.${id}`)}
              value={k === undefined ? '' : nf.format(k.value)}
              loading={loading}
              loadingLabel={t('ui.loading')}
              {...(k !== undefined ? { count: { value: k.value, format: (n: number) => nf.format(n) } } : {})}
              {...(k !== undefined ? { delta: { text: t('now.delta.vs', { value: formatNowDelta(k.delta, lang) }), direction: k.delta.direction, tone: k.delta.tone, srLabel: t('now.delta.sr') } } : {})}
            />
          );
        })}
        <StatCard
          variant="overview"
          label={t('now.kpi.supply')}
          value={view === undefined ? '' : nf.format(view.supplyNow.AVAILABLE + view.supplyNow.IN_RIDE)}
          hint={t('now.kpi.supply.hint')}
          loading={loading}
          loadingLabel={t('ui.loading')}
          {...(view !== undefined ? { count: { value: view.supplyNow.AVAILABLE + view.supplyNow.IN_RIDE, format: (n: number) => nf.format(n) } } : {})}
        />
      </Grid>

      <Text as="p" role="caption" tone="secondary">{t('now.overlap')}</Text>
      <Grid min="md" gap={4}>
        <Panel>
          <SectionTitle level={3} size="sub">{t('now.chart.hours')}</SectionTitle>
          <ColumnChart
            title={t('now.chart.hours')}
            categories={bars?.categories ?? []}
            series={[{ key: 'requested', label: t('lper.chart.total'), values: bars?.requested ?? [] }]}
            {...(bars?.currentIndex !== undefined ? { currentIndex: bars.currentIndex } : {})}
            formatValue={(n) => nf.format(n)}
            loading={loading}
            loadingLabel={t('ui.loading')}
            emptyLabel={t('now.chart.empty')}
            height={200}
          />
          {view !== undefined && <Text as="p" role="caption" tone="secondary">{t('now.timezone', { zone: view.timezone })}</Text>}
        </Panel>
        <Panel>
          <SectionTitle level={3} size="sub">{t('now.chart.supply')}</SectionTitle>
          {view !== undefined && !hasSupplyData(view) ? (
            <Text as="p" role="caption" tone="secondary">{t('now.chart.supply.unavailable')}</Text>
          ) : (
            <ColumnChart
              title={t('now.chart.supply')}
              categories={supply.map((h) => h.label)}
              series={[{ key: 'drivers', label: t('now.chart.supply.name'), values: supply.map((h) => h.driversOnline ?? 0) }]}
              formatValue={(n) => nf.format(n)}
              loading={loading}
              loadingLabel={t('ui.loading')}
              emptyLabel={t('now.chart.supply.none')}
              height={200}
            />
          )}
          {view !== undefined && hasSupplyData(view) && <Text as="p" role="caption" tone="secondary">{t('now.chart.supply.note')}</Text>}
        </Panel>
      </Grid>
      {view !== undefined && view.fiveMinute.status === 'UNAVAILABLE' && <Text as="p" role="caption" tone="secondary">{t('now.fiveMinute')}</Text>}
    </Stack>
  );
}
